import axios, { AxiosResponse } from 'axios';
import { Dispatch } from 'redux';

import { resetCart } from './action_cart';
import { resetGeskusIncentive } from './action_geskus_incentive';
import { resetDiscountCode } from './action_discount_code';
import { resetAdditionalData } from '../reducers/additionalDataSlice';
import { setCompanyName } from './action_company';
import { clearParentBrowserState } from '../utils/parentBrowserState';
import { csrfHeaders } from '../utils/csrf';
import { resetImageIntentSession } from '../utils/imageIntentReporter';

export const SET_AUTH_TOKEN = 'SET_AUTH_TOKEN';
export const LOGIN_PARENT = 'LOGIN_PARENT';
export const LOGOUT_PARENT = 'LOGOUT_PARENT';
export const CLEAR = 'CLEAR';
export const SIGNUP_PARENT_STEP_1 = 'SIGNUP_PARENT_STEP_1';
export const SIGNUP_PARENT_STEP_2 = 'SIGNUP_PARENT_STEP_2';
export const SET_CURRENT_USER = 'SET_CURRENT_USER';

const apiUrl = '/api/v2';

// These action creators hand redux-promise a promise as the payload; it
// dispatches the settled action (see reducers/reducer_auth).
interface PromiseAction {
  type: string;
  payload: Promise<AxiosResponse>;
}

// The single logout path — every caller goes through this.
//
// LOGOUT_PARENT resets the whole store to undefined (see reducers/index.js),
// and `company` is on the redux-persist blacklist, so the studio's name is
// only ever set at bootstrap and does not come back on its own. App renders
// `<div className="loading" />` whenever companyName is blank, so a logout
// that does not restore it leaves the parent staring at a blank spinner.
// Pass the name in from the caller, which still has it, and re-set it once
// the wipe has happened.
export const logoutAndReset =
  (companyName?: string) =>
  (dispatch: Dispatch): Promise<AxiosResponse> => {
    dispatch(resetCart());
    dispatch(resetGeskusIncentive());
    dispatch(resetDiscountCode());
    dispatch(resetAdditionalData());

    // Redux is wiped by the reducer; gallery keys and entry-point cookies live
    // outside it and have to be cleared by hand.
    clearParentBrowserState();
    // So does the image-intent tab session. The report stitches a session's
    // anonymous attempts to the parent who signs in later in it, so the next
    // person at this tab gets a new one (see resetImageIntentSession). Login
    // never resets it: attempts made before signing in stay stitchable.
    resetImageIntentSession();

    // redux-promise resolves the payload before the action reaches the
    // reducer, so what comes back here is the settled response, not the action.
    return Promise.resolve(
      dispatch(logoutParent()) as unknown as Promise<AxiosResponse>,
    ).then((response) => {
      if (companyName) dispatch(setCompanyName(companyName));

      return response;
    });
  };

export function setAuthToken(token: string) {
  return {
    type: SET_AUTH_TOKEN,
    payload: token,
  };
}

export function loginParent(data: Record<string, unknown>): PromiseAction {
  const request = axios.post('/login', data);

  return {
    type: LOGIN_PARENT,
    payload: request,
  };
}

export function logoutParent(): PromiseAction {
  // Current clients use DELETE with Rails CSRF protection. The server's
  // restricted GET bridge is temporary compatibility for already-open old tabs;
  // navigation/prefetch must never be able to sign a parent out.
  const request = axios.delete('/logout', { headers: csrfHeaders() });

  return {
    type: LOGOUT_PARENT,
    payload: request,
  };
}

export function signupParentStep1(
  data: Record<string, unknown>,
): PromiseAction {
  const request = axios.post(`${apiUrl}/parents`, data);

  return {
    type: SIGNUP_PARENT_STEP_1,
    payload: request,
  };
}

export function signupParentStep2(
  data: Record<string, unknown>,
): PromiseAction {
  const request = axios.patch(`${apiUrl}/parents/signup/step-2`, data);

  return {
    type: SIGNUP_PARENT_STEP_2,
    payload: request,
  };
}

export function setCurrentUser(): PromiseAction {
  const request = axios.get(`${apiUrl}/current-user`);

  return {
    type: SET_CURRENT_USER,
    payload: request,
  };
}
